import { GridRowProps } from '@progress/kendo-react-grid';
import { cloneElement, DragEvent, ReactElement, useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';
import { SHOPPING_LIST_REORDER_MIME } from './shoppingListReorder';

const DROP_TARGET_CLASS = 'shopping-list-row-drop-target';

const clearDropTargetHighlights = () => {
  document
    .querySelectorAll(`.${DROP_TARGET_CLASS}`)
    .forEach((element) => element.classList.remove(DROP_TARGET_CLASS));
};

export const useShoppingListReorderRowRender = () => {
  const canReorderRows = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.canReorderRows
  );
  const onReorderProducts = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onReorderProducts
  );

  return useCallback(
    (row: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
      if (!canReorderRows) {
        return row;
      }

      const productKey = String(props.dataItem?.productKey ?? '');

      const handleDragOver = (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        const currentRow = event.currentTarget;
        if (!currentRow.classList.contains(DROP_TARGET_CLASS)) {
          clearDropTargetHighlights();
          currentRow.classList.add(DROP_TARGET_CLASS);
        }
      };

      const handleDragLeave = (event: DragEvent<HTMLTableRowElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          event.currentTarget.classList.remove(DROP_TARGET_CLASS);
        }
      };

      const handleDrop = (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const fromKey =
          event.dataTransfer.getData(SHOPPING_LIST_REORDER_MIME) ||
          event.dataTransfer.getData('text/plain');

        if (fromKey && productKey) {
          onReorderProducts(fromKey, productKey);
        }

        clearDropTargetHighlights();
      };

      return cloneElement(row, {
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        className: [row.props.className, 'shopping-list-reorderable-row'].filter(Boolean).join(' '),
      });
    },
    [canReorderRows, onReorderProducts]
  );
};
